import type { ErrorComponentProps } from '@tanstack/react-router'

import { isPaymentAlreadyInitiatedError } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { Link, useRouterState } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { AlertCircle, CreditCard, Plane, RefreshCw, Search } from 'lucide-react'

export function BookingError({ error, reset }: ErrorComponentProps) {
  const routerState = useRouterState()
  const sessionId = (routerState.location.search as { session?: string }).session

  if (isPaymentAlreadyInitiatedError(error) && sessionId) {
    return (
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="flex w-full max-w-md flex-col items-center text-center">
          <img alt="Massilia Voyages" className="h-10" src="/logos/massilia-logo.png" />
          <div className="bg-gray-light-200 mt-8 h-px w-16" />
          <div className="mt-8 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
            <AlertCircle className="h-8 w-8 text-amber-500" />
          </div>
          <h1 className="text-gray-light-900 mt-6 text-xl font-semibold">Paiement déjà initié</h1>
          <p className="text-gray-light-500 mt-2 text-sm leading-relaxed">
            Le paiement a déjà été initié pour cette réservation. Il n&apos;est plus possible de
            modifier les informations concernant les passagers ou l&apos;assurance. Cliquez sur le
            bouton ci-dessous pour reprendre le paiement.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button variant="secondary" asChild>
              <Link to="/recherche">
                <Plane className="h-4 w-4" />
                Retour à la recherche
              </Link>
            </Button>
            <Button asChild className="bg-brand-600 hover:bg-brand-700">
              <Link search={{ session: sessionId }} to="/paiement">
                <CreditCard className="h-4 w-4" />
                Reprendre le paiement
              </Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const { message, title } = getErrorDisplay(error)
  const retryable = isRetryableError(error)

  return (
    <div className="flex flex-1 items-center justify-center px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center text-center">
        {/* Logo */}
        <img alt="Massilia Voyages" className="h-10" src="/logos/massilia-logo.png" />

        {/* Separator */}
        <div className="bg-gray-light-200 mt-8 h-px w-16" />

        {/* Error icon */}
        <div className="mt-8 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <AlertCircle className="h-8 w-8 text-red-500" />
        </div>

        {/* Title */}
        <h1 className="text-gray-light-900 mt-6 text-xl font-semibold">{title}</h1>

        {/* Apology */}
        <p className="text-gray-light-500 mt-2 text-sm leading-relaxed">
          Nous sommes désolés pour ce désagrément. Notre équipe fait tout pour vous offrir la
          meilleure expérience.
        </p>

        {/* Backend error detail */}
        <div className="border-gray-light-200 bg-gray-light-50 mt-6 w-full rounded-xl border px-5 py-4">
          <p className="text-gray-light-700 text-sm leading-relaxed">{message}</p>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {retryable && (
            <Button className="bg-brand-600 hover:bg-brand-700" onClick={reset}>
              <RefreshCw className="h-4 w-4" />
              Réessayer
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/recherche">
              <Search className="h-4 w-4" />
              Relancer la recherche
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

function getErrorDisplay(error: Error): { message: string; title: string } {
  if (error instanceof AxiosError) {
    const backendMessage = error.response?.data?.message
    if (typeof backendMessage === 'string') {
      return {
        message: backendMessage,
        title: 'Erreur',
      }
    }
  }

  return {
    message:
      'Une erreur est survenue lors de votre réservation. Veuillez réessayer ou relancer votre recherche.',
    title: 'Erreur inconnue',
  }
}

function isRetryableError(error: Error): boolean {
  if (!(error instanceof AxiosError)) {
    return true
  }

  const status = error.response?.status

  if (!status) {
    return true
  }

  if (status >= 500) {
    return true
  }

  if (status === 409) {
    return true
  }

  return false
}
